function _extends() { _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }

function _slicedToArray(arr, i) { return _arrayWithHoles(arr) || _iterableToArrayLimit(arr, i) || _unsupportedIterableToArray(arr, i) || _nonIterableRest(); }

function _nonIterableRest() { throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); }

function _unsupportedIterableToArray(o, minLen) { if (!o) return; if (typeof o === "string") return _arrayLikeToArray(o, minLen); var n = Object.prototype.toString.call(o).slice(8, -1); if (n === "Object" && o.constructor) n = o.constructor.name; if (n === "Map" || n === "Set") return Array.from(o); if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen); }

function _arrayLikeToArray(arr, len) { if (len == null || len > arr.length) len = arr.length; for (var i = 0, arr2 = new Array(len); i < len; i++) { arr2[i] = arr[i]; } return arr2; }

function _iterableToArrayLimit(arr, i) { var _i = arr == null ? null : typeof Symbol !== "undefined" && arr[Symbol.iterator] || arr["@@iterator"]; if (_i == null) return; var _arr = []; var _n = true; var _d = false; var _s, _e; try { for (_i = _i.call(arr); !(_n = (_s = _i.next()).done); _n = true) { _arr.push(_s.value); if (i && _arr.length === i) break; } } catch (err) { _d = true; _e = err; } finally { try { if (!_n && _i["return"] != null) _i["return"](); } finally { if (_d) throw _e; } } return _arr; }

function _arrayWithHoles(arr) { if (Array.isArray(arr)) return arr; }

import React from 'react';
import styled from 'styled-components';
import ReactDOM from 'react-dom';
import { animated, useSpring } from '@react-spring/web';
import { useDrag } from 'react-use-gesture';
import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock';
import { useMedia } from 'use-media';
import { strictSpace } from '../../utils';
import { projection, rubberBandIfOutOfBounds, findNearestNumberInArray, rangeMap } from './utilities';
import usePortalContainer from '../../utils/portal';
import Overlay from '../Overlay';
var threshold = 10;
var config = {
  tension: 439,
  friction: 40
};
export var StyledDrawer = styled.div.withConfig({
  displayName: "Drawer__StyledDrawer",
  componentId: "sc-b4hkh3-0"
})(["background-color:", ";padding-top:", ";bottom:0;padding-bottom:", ";z-index:1000;position:fixed;left:0;right:0;border-top-left-radius:", ";border-top-right-radius:", ";will-change:transform;touch-action:none;user-select:none;white-space:pre-line;overflow:auto;", ";@media screen and (min-width:600px){min-width:400px;width:min-content;top:50%;left:50%;bottom:auto;border-bottom-left-radius:", ";border-bottom-right-radius:", ";}"], function (_ref) {
  var theme = _ref.theme;
  return theme.colors.bgElevated;
}, function (_ref2) {
  var theme = _ref2.theme;
  return theme.space.s12;
}, function (_ref3) {
  var theme = _ref3.theme;
  return theme.space.s24;
}, function (_ref4) {
  var theme = _ref4.theme;
  return theme.borderRadius.radius24;
}, function (_ref5) {
  var theme = _ref5.theme;
  return theme.borderRadius.radius24;
}, strictSpace, function (_ref6) {
  var theme = _ref6.theme;
  return theme.borderRadius.radius24;
}, function (_ref7) {
  var theme = _ref7.theme;
  return theme.borderRadius.radius24;
});

var Drawer = function Drawer(_ref8) {
  var onDismiss = _ref8.onDismiss,
      children = _ref8.children;
  var renderTimeRef = React.useRef(new Date());
  var isWideInit = window.matchMedia('(min-width: 600px)');
  var isWide = useMedia({
    minWidth: '600px'
  }, isWideInit.matches);

  var _useSpring = useSpring(function () {
    return {
      y: 0,
      config: config
    };
  }),
      _useSpring2 = _slicedToArray(_useSpring, 2),
      y = _useSpring2[0].y,
      animate = _useSpring2[1];

  var yStops = React.useRef([0, 0]);
  var containerRef = React.useRef(null);
  var overlayRef = React.useRef(null);
  var container = usePortalContainer();
  React.useLayoutEffect(function () {
    var _overlayRef$current;

    if (!containerRef.current || !overlayRef.current) return;
    yStops.current = isWide ? [-containerRef.current.clientHeight / 2, ((_overlayRef$current = overlayRef.current) === null || _overlayRef$current === void 0 ? void 0 : _overlayRef$current.clientHeight) / 2] : [0, containerRef.current.clientHeight];
    animate({
      y: yStops.current[1],
      immediate: true
    });
  }, [isWide, animate]);
  React.useEffect(function () {
    animate({
      y: yStops.current[0],
      immediate: false
    });
  }, [animate, isWide]);

  var closeDrawer = function closeDrawer() {
    return animate({
      y: yStops.current[1],
      onRest: onDismiss
    });
  };

  var bind = useDrag(function (_ref9) {
    var last = _ref9.last,
        _ref9$movement = _slicedToArray(_ref9.movement, 2),
        movementY = _ref9$movement[1],
        _ref9$vxvy = _slicedToArray(_ref9.vxvy, 2),
        velocityY = _ref9$vxvy[1],
        memo = _ref9.memo;

    if (isWide) return;

    if (!memo) {
      var isIntentionalGesture = Math.abs(movementY) > threshold;
      if (!isIntentionalGesture) return; // @ts-ignore

      memo = y.get() - movementY;
    }

    if (!containerRef.current) return;
    if (!yStops.current.length) return;
    disableBodyScroll(containerRef.current);

    if (last) {
      enableBodyScroll(containerRef.current); // @ts-ignore

      var projectedEndpoint = y.get() + projection(velocityY);
      var point = findNearestNumberInArray(projectedEndpoint, yStops.current);
      var notificationClosed = point === yStops.current[1];
      return animate({
        y: notificationClosed ? yStops.current[1] : yStops.current[0],
        onRest: notificationClosed ? onDismiss : function () {},
        immediate: false
      });
    }

    var newY = rubberBandIfOutOfBounds(yStops.current[0], yStops.current[1], memo + movementY, 0.06);
    animate({
      y: newY,
      immediate: true
    });
    return memo;
  });
  return /*#__PURE__*/ReactDOM.createPortal( /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(StyledDrawer, _extends({}, bind(), {
    ref: containerRef,
    as: animated.div,
    style: {
      x: isWide ? '-50%' : '0',
      y: y
    }
  }), typeof children === 'function' ? children({
    closeDrawer: closeDrawer
  }) : children), /*#__PURE__*/React.createElement(Overlay, {
    ref: overlayRef,
    as: animated.div,
    onClick: function onClick() {
      var minMS = 100; // require 100 ms to pass after render to make it less likely to accidentally trigger close

      if (Date.now() - renderTimeRef.current.getTime() > minMS) closeDrawer();
    },
    style: {
      opacity: y.to(function (y) {
        // Magic number: 0.64 -> the default opacity of the backdrop
        var opacity = rangeMap(yStops.current, [0.64, 0], y);
        return isNaN(opacity) ? 0 : opacity;
      })
    }
  })), container);
};
/** @component */


export default Drawer;